import {Carousel, useCarousel} from '@codeurs/carousel'
import {DPArticle, DPArticlesBlock, DPPress} from 'types/DPTypes'

import {Icon} from 'assets/icon'
import {Block} from 'blocks/block'
import {CarouselControls} from 'layout/carousel/carouselcontrols'
import {Imagepreview} from 'layout/preview/imagepreview'
import {Theme} from 'layout/theme'
import React from 'react'
import {CarouselType} from 'types/basictypes'
import {useTranslation} from 'util/i18'
import {Link} from 'util/link'
import {useLinks} from 'util/links'
import {fromModule} from 'util/styler/Styler'
import {useIDFromAnchorBlock} from 'util/useAnchor'
import css from './articles.module.scss'

const styles = fromModule(css)

export const Articles: React.FC<DPArticlesBlock> = (data) => {
	const {_type, title, articles, populair_label, populair_tags} = data
	const carousel = useCarousel()
	const {readingroom} = useLinks()
	const id = useIDFromAnchorBlock(data)

	return (
		<Block type={_type} className={styles.articles()} anchorID={id}>
			<Theme.Container>
				<div className={styles.articles.top()}>
					<div className={styles.articles.top.title()}>
						<Link href={readingroom.url}>
							<Theme.H2 mod={['blueline', 'arrow']}>
								{title || readingroom.title}
							</Theme.H2>
						</Link>
					</div>
					{carousel && carousel.total > 1 && (
						<div className={styles.articles.top.nav()}>
							<CarouselControls carousel={carousel} />
						</div>
					)}
				</div>
				<ArticlesSubjects label={populair_label} tags={populair_tags} />
			</Theme.Container>
			<ArticlesContent carousel={carousel} articles={articles} />
		</Block>
	)
}

const ArticlesSubjects: React.FC<{
	label: string
	tags: Array<{name?: string}>
}> = (data) => {
	const {label, tags} = data
	const t = useTranslation()
	const {readingroom} = useLinks()
	const tagUrl = readingroom.url + '?tags='

	if (!tags || tags.length === 0) return null

	return (
		<div className={styles.subjects()}>
			{label && <Theme.H5>{label}</Theme.H5>}
			<div className={styles.subjects.list()}>
				{tags.map((tag, i) => (
					<div key={i} className={styles.subjects.list.item()}>
						<div className={styles.subjects.list.item.tag()}>
							<Link
								href={tagUrl + tag.name}
								className={styles.subjects.list.item.tag.label()}
							>
								{tag.name}
							</Link>
						</div>
					</div>
				))}
				<div className={styles.subjects.list.item()}>
					<div className={styles.subjects.list.item.tag.mod('link')()}>
						<Theme.Link href={readingroom.url + '?open=1'}>
							<Theme.LinkText>{t.words.show_all}</Theme.LinkText>
							<Theme.LinkIcon />
						</Theme.Link>
					</div>
				</div>
			</div>
		</div>
	)
}

const ArticlesContent: React.FC<{
	carousel: CarouselType
	articles: Array<DPArticle | DPPress>
}> = (data) => {
	const {carousel, articles} = data
	if (!articles || articles.length === 0) return null

	return (
		<div className={styles.content()}>
			<div className={styles.content.background()}>
				<Theme.Container>
					<div className={styles.content.items()}>
						<Carousel {...carousel} full>
							{articles.map((item, i) => (
								<div
									key={i}
									className={styles.content.items.item.is({
										active: carousel.isActive(i)
									})()}
								>
									<Imagepreview
										tabIndex={carousel.isActive(i) ? 0 : -1}
										{...item}
									/>
								</div>
							))}
							<div className={styles.content.items.item()}>
								<ArticlesContentExtra />
							</div>
						</Carousel>
					</div>
				</Theme.Container>
			</div>
		</div>
	)
}

const ArticlesContentExtra: React.FC = () => {
	const {readingroom} = useLinks()
	const t = useTranslation()

	return (
		<Link href={readingroom.url} className={styles.contentextra()}>
			<div className={styles.contentextra.content()}>
				<div className={styles.contentextra.content.icon()}>
					<Icon icon="Book" />
				</div>
				<Theme.H4 className={styles.contentextra.content.title()} mod="light">
					{t.article.readmore}
				</Theme.H4>
			</div>
		</Link>
	)
}
